import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import es from "./locales/es.json";
import en from "./locales/en.json";
import ca from "./locales/ca.json";
import eu from "./locales/eu.json";
import gl from "./locales/gl.json";
import fr from "./locales/fr.json";
import it from "./locales/it.json";
import de from "./locales/de.json";
import pt from "./locales/pt.json";
import zh from "./locales/zh.json";
import ru from "./locales/ru.json";

// Configuración inicial con todos los idiomas disponibles (incl. los añadidos en creator)
const allLanguages = ["es", "ca", "eu", "gl", "en", "fr", "it", "de", "pt", "zh", "ru"];

/**
 * Cadena de reserva para traducciones: nunca un solo idioma "regional" aislado,
 * porque ca/eu/gl suelen tener menos claves que `es` y, si el default del sitio
 * es catalán, `fallbackLng: "ca"` hacía que faltasen claves y no cayeran al
 * castellano (i18n volvía a probar "ca" y el texto quedaba vacío o el key id).
 */
function buildTranslationFallbackChain(defaultLanguage) {
	const def =
		defaultLanguage && String(defaultLanguage).split("-")[0].toLowerCase() || "es";
	const pool = [def, "es", "en"];
	const seen = new Set();
	return pool.filter((code) => {
		if (seen.has(code)) return false;
		seen.add(code);
		return true;
	});
}

i18n
	.use(LanguageDetector)
	.use(initReactI18next)
	.init({
		resources: {
			es: { translation: es },
			ca: { translation: ca },
			eu: { translation: eu },
			gl: { translation: gl },
			en: { translation: en },
			fr: { translation: fr },
			it: { translation: it },
			de: { translation: de },
			pt: { translation: pt },
			zh: { translation: zh },
			ru: { translation: ru },
		},
		supportedLngs: allLanguages,
		fallbackLng: {
			default: ["es"],
			ca: ["es"],
			eu: ["es"],
			gl: ["es"],
			en: ["es"],
			fr: ["es"],
			it: ["es"],
			de: ["es"],
			pt: ["es"],
			zh: ["es"],
			ru: ["es"],
		},
		nonExplicitSupportedLngs: true,
		detection: {
			order: ["localStorage", "querystring", "navigator"],
			caches: ["localStorage"],
			lookupQuerystring: "lng",
			lookupLocalStorage: "i18nextLng",
			checkWhitelist: true,
		},
		interpolation: { escapeValue: false },
		initImmediate: false,
	});

// Función para actualizar los idiomas soportados según platformSettings
export const updateSupportedLanguages = (activeLanguages, defaultLanguage) => {
	if (
		!activeLanguages ||
		!Array.isArray(activeLanguages) ||
		activeLanguages.length === 0
	) {
		// Si no hay activeLanguages, usar todos los idiomas disponibles
		i18n.options.supportedLngs = allLanguages;
		i18n.options.fallbackLng = buildTranslationFallbackChain(defaultLanguage);
		return;
	}

	// Filtrar solo los idiomas que están en activeLanguages
	const supported = allLanguages.filter((lang) =>
		activeLanguages.includes(lang)
	);

	if (supported.length > 0) {
		i18n.options.supportedLngs = supported;
		i18n.options.fallbackLng = buildTranslationFallbackChain(defaultLanguage);

		// Obtener el idioma actual y los idiomas detectados
		const currentLang = i18n.language || i18n.languages[0];
		const detectedLangs = i18n.languages || [];

		// Función auxiliar para normalizar códigos de idioma
		const normalizeLang = (lang) => lang.split("-")[0].toLowerCase();

		// Buscar si algún idioma detectado del navegador está en los soportados
		// Primero verificar i18n.languages (ya normalizados por i18next)
		let detectedSupportedLang = detectedLangs.find((lang) => {
			const baseLang = normalizeLang(lang);
			return supported.includes(baseLang);
		});

		// Si no se encontró, verificar directamente navigator.language y navigator.languages
		if (!detectedSupportedLang && typeof navigator !== "undefined") {
			// Verificar navigator.language
			if (navigator.language) {
				const baseLang = normalizeLang(navigator.language);
				if (supported.includes(baseLang)) {
					detectedSupportedLang = baseLang;
				}
			}

			// Si aún no se encontró, verificar navigator.languages
			if (!detectedSupportedLang && navigator.languages) {
				for (const navLang of navigator.languages) {
					const baseLang = normalizeLang(navLang);
					if (supported.includes(baseLang)) {
						detectedSupportedLang = baseLang;
						break;
					}
				}
			}
		}

		// Si el idioma actual no está en los soportados
		if (!supported.includes(currentLang)) {
			// Si hay un idioma detectado del navegador que está soportado, usarlo
			if (detectedSupportedLang) {
				const baseLang =
					typeof detectedSupportedLang === "string"
						? normalizeLang(detectedSupportedLang)
						: detectedSupportedLang;
				if (supported.includes(baseLang)) {
					i18n.changeLanguage(baseLang);
					return;
				}
			}
			// Si no, usar el defaultLanguage
			i18n.changeLanguage(defaultLanguage || "es");
		}
	}
};

export default i18n;
